import { Factory } from "@/data/dao/Factory";
import { RequestCache } from "@/data/dao/RequestCache";
import { TableDao } from "@/data/dao/service/TableDao";
import { DataOrigin, PermalinkMode } from "@/data/model/Enums";
import type { ITable } from "@/data/model/Table";
import { Misc } from "@/data/util/Misc";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { portalEvents } from "@/portal/events/portalEvents";
import { TastierGlobal } from "@/portal/globalAccess";
import { TableBulkWindow } from "@/portal/modal/TableBulkWindow";
import { UiNotification } from "@/portal/ui/Notification";

export class TableTableController {
    private $element: JQuery;
    private readonly dao: TableDao;
    private bulkWindow?: TableBulkWindow;

    private dataTable: DataTable<ITable>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<TableDao>(TableDao);

        const tableConfig: IDataTableConfig<ITable> = {
            columnMapping: [
                {
                    name: i18n("general.fields.name"),
                    property: "name",
                },
                {
                    name: i18n("table.fields.tableNumber"),
                    property: "tableNumber",
                    styles: { width: "15%" },
                },
                {
                    name: i18n("table.fields.seats"),
                    property: "seats",
                    styles: { width: "15%" },
                },
                {
                    name: i18n("table.fields.requireInfo"),
                    property: "requireInfo",
                    styles: { width: "15%" },
                    formatter: (m, p) => (p ? i18n("general.yes") : i18n("general.no")),
                },
                {
                    name: i18n("general.entities.room"),
                    property: "roomName",
                    styles: { width: "22%" },
                },
                {
                    name: i18n("general.fields.state"),
                    property: "isActive",
                    styles: { width: "8%" },
                    formatter: (m, p) => {
                        return (
                            '<div class="kt-badge ' +
                            (p ? "kt-badge--success" : "kt-badge--danger") +
                            ' kt-badge--inline">' +
                            (p ? i18n("general.fields.active") : i18n("general.fields.inactive")) +
                            "</div>"
                        );
                    },
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "110px", "text-align": "right" },
                    formatter: () => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
            defaultSortAsc: true,
            locationDependend: true,
            defaultSortProperty: "tableNumber",
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.search.addDropdownFilter("roomId", RequestCache.roomDataSource(), true, i18n("table.allRooms"));
        this.search.addDropdownFilterFromEnum(
            "dataOrigin",
            DataOrigin,
            "enums.DataOrigin",
            i18n("table.allDataOrigins")
        );
        this.dataTable = new DataTable<ITable>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao,
            tableConfig
        );
        this.dataTable.onClick(
            (m) => {
                location.href = "/table/" + m.id;
            },
            (m) => {
                window.open("/table/" + m.id);
            }
        );

        this.dataTable.listenTo(this.search);
        portalEvents.OnLocationChanged.add(async () => {
            await this.search.refreshDropdownFilter("roomId");
            this.dataTable.loadData(true);

            if (this.bulkWindow !== undefined) this.bulkWindow.needsTableReload = true;
        }, this);

        this.$element.find("> .kt-portlet__head > .kt-portlet__head-toolbar .btn.bulk").click((e) => {
            e.preventDefault();
            this.onBulkEdit();
        });

        this.$element.find("> .kt-portlet__head > .kt-portlet__head-toolbar .btn.qr").click((e) => {
            e.preventDefault();
            const modeOptions: Record<string, string> = {};
            Misc.eachEnum(PermalinkMode, (i, e) => {
                modeOptions[i.toString()] = i18n("enums.PermalinkMode." + e);
            });

            UiNotification.confirmWithSelect({
                message: i18n("table.downloadAllQrText"),
                title: i18n("table.downloadAllQr"),
                selectOptions: modeOptions,
                selectedValue: PermalinkMode.None.toString(),
                confirmed: (mode) => {
                    location.href =
                        "/api/table/bulkexport?locationId=" +
                        TastierGlobal.getLocationSwitch()!.getCurrentLocationId() +
                        "&mode=" +
                        mode +
                        "&c=" +
                        new Date().getTime();
                },
                confirmBtn: i18n("table.downloadBtn"),
            });
        });
    }

    private onBulkEdit() {
        if (this.bulkWindow === undefined) {
            this.bulkWindow = new TableBulkWindow();
            this.bulkWindow.onExecuted = () => this.dataTable.loadData(true);
        }

        this.bulkWindow.show();
    }
}
